import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { buildIdentity, UPDATE_CHECK_INTERVAL_MS } from './app-update.js';
import { appBuildSchema } from '@speaking-coach/shared-types/src/app-build.js';
import { CLIENT_CONTRACT } from '@speaking-coach/shared-types/src/client-compatibility.js';
const noSubscribe = () => () => { };
const emptySnapshot = () => null;
const online = () => navigator.onLine !== false;
function subscribeOnline(listener) {
    window.addEventListener('online', listener);
    window.addEventListener('offline', listener);
    return () => { window.removeEventListener('online', listener); window.removeEventListener('offline', listener); };
}
/** Required state is independent of optional snooze, but uses the same app safety. */
export function useRequiredAppUpdate({ running, account, load, safety, canRefresh, storageKey, reload, compatibility }) {
    const policy = useSyncExternalStore(compatibility?.state.subscribe ?? noSubscribe, compatibility?.state.getSnapshot ?? emptySnapshot, emptySnapshot);
    const safe = useSyncExternalStore(safety.subscribe, safety.isSafe, () => false);
    const connected = useSyncExternalStore(subscribeOnline, online, () => false);
    const [target, setTarget] = useState(null);
    const [attempted, setAttempted] = useState([]);
    const attempts = useRef(new Set());
    const current = useRef({ account, canRefresh, reload });
    useLayoutEffect(() => { current.current = { account, canRefresh, reload }; });
    const identity = buildIdentity(running);
    const attemptKey = `${storageKey}:required:${identity}`;
    const checkRef = useRef(() => { });
    useEffect(() => {
        if (!compatibility || !account || !identity) {
            setTarget(null);
            return;
        }
        try {
            const saved = JSON.parse(sessionStorage.getItem(attemptKey) ?? '[]');
            const values = Array.isArray(saved) ? saved.filter((v) => typeof v === 'string') : [];
            attempts.current = new Set(values);
            setAttempted(values);
        }
        catch {
            attempts.current = new Set();
            setAttempted([]);
        }
        let disposed = false;
        let flight = false;
        let lastCheck = -Infinity;
        let request = null;
        const check = (force = false) => {
            if (disposed || flight || !compatibility.state.required() || !online() || document.visibilityState === 'hidden' || (!force && Date.now() - lastCheck < UPDATE_CHECK_INTERVAL_MS))
                return;
            flight = true;
            lastCheck = Date.now();
            request = new AbortController();
            const controller = request;
            void (async () => {
                let timer;
                try {
                    const timeout = new Promise((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('Compatibility check timed out')); }, 5000); });
                    const value = await Promise.race([compatibility.load(controller.signal), timeout]);
                    if (disposed || controller.signal.aborted)
                        return;
                    compatibility.state.observe(value);
                    if (!compatibility.state.required()) {
                        setTarget(null);
                        return;
                    }
                    const metadata = await Promise.race([load(controller.signal), timeout]);
                    if (disposed || controller.signal.aborted)
                        return;
                    const build = appBuildSchema.safeParse(metadata);
                    const contract = metadata && typeof metadata === 'object' && 'clientContract' in metadata ? metadata.clientContract : null;
                    setTarget(build.success && typeof contract === 'number' && Number.isInteger(contract) && contract >= 0 ? { build: build.data, contract } : null);
                }
                catch {
                    // A failed artifact check cannot leave an obsolete refresh target actionable.
                    if (!disposed)
                        setTarget(null);
                }
                finally {
                    clearTimeout(timer);
                    flight = false;
                }
            })();
        };
        checkRef.current = () => {
            if (flight || !online() || document.visibilityState === 'hidden')
                return;
            // An explicit retry can recover after a lagging edge served stale HTML.
            // Background checks never clear this guard or perform a reload.
            attempts.current.clear();
            setAttempted([]);
            setTarget(null);
            try {
                sessionStorage.removeItem(attemptKey);
            }
            catch { /* Optional storage. */ }
            check(true);
        };
        const onResume = () => check();
        const unsubscribe = compatibility.state.subscribe(() => { if (compatibility.state.required())
            check(true); });
        check();
        const timer = window.setInterval(() => check(true), UPDATE_CHECK_INTERVAL_MS);
        window.addEventListener('focus', onResume);
        window.addEventListener('online', onResume);
        window.addEventListener('pageshow', onResume);
        document.addEventListener('visibilitychange', onResume);
        return () => {
            disposed = true;
            request?.abort();
            clearInterval(timer);
            unsubscribe();
            checkRef.current = () => { };
            window.removeEventListener('focus', onResume);
            window.removeEventListener('online', onResume);
            window.removeEventListener('pageshow', onResume);
            document.removeEventListener('visibilitychange', onResume);
        };
    }, [account, identity, compatibility?.state, compatibility?.load, load, attemptKey]);
    const targetId = target ? buildIdentity(target.build) : null;
    const usable = !!target && !!targetId && targetId !== identity && !!policy?.supported.includes(target.contract) && !attempted.includes(targetId);
    const allowed = () => !!account && current.current.account === account && safety.isSafe() && online() && document.visibilityState !== 'hidden' && current.current.canRefresh();
    const refresh = useCallback(() => {
        const latestPolicy = compatibility?.state.getSnapshot();
        if (!usable || !targetId || !allowed() || !target || !latestPolicy?.supported.includes(target.contract) || latestPolicy.supported.includes(CLIENT_CONTRACT))
            return;
        const next = [...attempted, targetId];
        // Update synchronously too: a second click before React commits must not reload twice.
        if (attempts.current.has(targetId))
            return;
        attempts.current.add(targetId);
        try {
            sessionStorage.setItem(attemptKey, JSON.stringify(next));
        }
        catch { /* Optional tab-session loop protection. */ }
        setAttempted(next);
        current.current.reload();
    }, [usable, targetId, target, attempted, attemptKey, account, safety, compatibility?.state]);
    if (!account || !identity || !policy || policy.supported.includes(CLIENT_CONTRACT))
        return null;
    const parsed = appBuildSchema.safeParse(running);
    return {
        status: !connected ? 'offline' : !safe || !canRefresh() ? 'busy' : usable ? 'ready' : 'waiting',
        builds: parsed.success && target ? { current: parsed.data, available: target.build } : null,
        refresh,
        check: () => checkRef.current(),
    };
}
//# sourceMappingURL=required-app-update.js.map